import { format } from "date-fns"
import { ButtonLink } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { cn } from "design-system/utils"
import { Pencil, SquareArrowOutUpRight } from "lucide-react"
import { createElement, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { S3_UPLOAD_INPUT_FORMATS } from "schema"
import z from "zod"
import whatsappBg from "./image.png"

type Props = {
  templateId?: string
  header?: string
  body: string
  footer?: string
  variables?: Array<{
    paramName: string
    example: string
  }> | null
  className?: string
  contentClassName?: string
  media?: {
    format?: z.infer<typeof S3_UPLOAD_INPUT_FORMATS> | null
    url?: string | null
  } | null
  buttonTexts?: (string | null | undefined)[]
}

const formatText = (text: string, variables?: Props["variables"]): ReactNode[] => {
  const pattern =
    /{{(\w+)}}|\*((?:{{\w+}}|[^*])+?)\*|_((?:{{\w+}}|[^_])+?)_|~((?:{{\w+}}|[^~])+?)~/g
  const parts: ReactNode[] = []
  let lastIndex = 0

  for (const match of text.matchAll(pattern)) {
    parts.push(text.slice(lastIndex, match.index))

    if (match[1] !== undefined) {
      const variable = variables?.find((variable) => variable.paramName === match[1])

      if (variable?.example) {
        parts.push(
          <Tooltip key={match.index}>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                className="cursor-help underline decoration-dashed decoration-muted-foreground/40 underline-offset-4"
              >
                {variable.example}
              </span>
            </TooltipTrigger>
            <TooltipContent>{`{{${variable.paramName}}}`}</TooltipContent>
          </Tooltip>,
        )
      } else {
        parts.push(match[0])
      }
    } else {
      let tag = "s"
      if (match[2] !== undefined) tag = "b"
      else if (match[3] !== undefined) tag = "i"

      const content = match[2] ?? match[3] ?? match[4]

      parts.push(createElement(tag, { key: match.index }, formatText(content ?? "", variables)))
    }

    lastIndex = match.index + match[0].length
  }

  parts.push(text.slice(lastIndex))

  return parts
}

const drawMedia = (media: Props["media"]) => {
  if (!media || !media.url || !media.format) return null

  switch (media.format) {
    case "IMAGE":
    case "GIF":
      return <img src={media.url} className="h-auto w-full inline-block rounded-sm" />
    default:
      return null
  }
}

export const WhatsappTemplatePreview = ({
  templateId,
  header,
  body,
  footer,
  variables,
  className,
  contentClassName,
  media,
  buttonTexts = [],
}: Props) => {
  const { t } = useTranslation()

  return (
    <div
      className={cn(
        "group/preview bg-emerald-50 dark:bg-emerald-900 p-3 w-full min-h-10 relative text-start text-wrap col-span-2 rounded-md overflow-hidden",
        className,
      )}
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-10 z-0 dark:opacity-20"
        style={{ backgroundImage: `url(${whatsappBg})` }}
      />
      {templateId && (
        <ButtonLink
          to="/settings/channels/whatsapp/templates/$id"
          params={{ id: templateId }}
          target="_blank"
          variant="outline"
          size="sm"
          className="absolute right-1 top-1 z-20 opacity-0 pointer-events-none transition-[opacity,transform] duration-150 ease-out group-hover/preview:translate-x-0 group-hover/preview:opacity-100 group-hover/preview:pointer-events-auto"
          onClick={(event) => event.stopPropagation()}
        >
          <Pencil />
          {t("shared.edit")}
        </ButtonLink>
      )}
      <div
        className={cn(
          "flex flex-col gap-2 bg-white dark:bg-secondary p-3 pb-1 rounded-md rounded-tl-none relative z-10 text-sm shadow-md whitespace-pre-wrap",
          contentClassName,
        )}
      >
        <div className="overflow-hidden w-3 h-8 absolute top-0 -left-3">
          <div className="bg-background dark:bg-secondary w-8 h-4 rotate-[60deg] absolute top-[2px] left-[4px]" />
        </div>
        {media && <div>{drawMedia(media)}</div>}
        {header && <span>{formatText(header)}</span>}
        <span>{formatText(body, variables)}</span>
        {footer && <span className="text-muted-foreground">{formatText(footer)}</span>}
        <span className="text-muted-foreground text-right text-[10px]">
          {format(new Date(), "HH:mm")}
        </span>
      </div>
      {buttonTexts.map((buttonText, index) => (
        <div
          key={index}
          className={cn(
            "mt-1 bg-white dark:bg-secondary rounded-md relative z-10 text-sm shadow-md",
            "text-primary font-medium py-2 flex items-center justify-center gap-1.5",
          )}
        >
          <SquareArrowOutUpRight className="h-4 w-4" />
          {buttonText || t("campaigns.templates.whatsapp.detail.config.blocks.openLinkFallback")}
        </div>
      ))}
    </div>
  )
}
